import { useEffect, useRef, useState } from "react";
import type { GroundConfig, GroundConfigPatch, GroundMaterialParams, PostFXSettings, Vec3 } from "../../../../shared/api-contract";
import type { DomainId } from "../../../../state/editor/types";
import { useDomainState, useEditorStore } from "../../../../state/editor/useEditor";
import { useViewer } from "../../../../state/ViewerProvider";
import { useCaps } from "../../CapsProvider";
import { CompactCheck, CompactColor, CompactNumber, CompactSlider, Row } from "../../inspector/rows";
import type { InertTag } from "../../inspector/schema";

const LIMIT_CAMERA_INERT: InertTag = {
  reason:
    "Stored, never read. GroundConfig.limitCameraAboveGround round-trips through getGroundConfig and the vjson reader; no camera clamp anywhere consults it.",
};
const PROG_COLOR_INERT: InertTag = {
  reason:
    "Stored, never read. GroundShadowBaker consumes every other progressiveShadow field; the cone accumulates a mono occlusion value, so the colour never reaches a pixel.",
};

/**
 * Full iJewel Ground panel (v18): plane props + the Randomized Light + the
 * Progressive Directional shadow light. Reads getGroundConfig, writes
 * setGroundConfig (canonical round-trip). Fields the engine implements wire
 * through; the rest are stored.
 */
const GROUND_MATERIAL_DEFAULT: Required<GroundMaterialParams> = { color: "#ffffff", roughness: 1, metalness: 0 };

/** Trailing window for the mirrored-slice re-read (see syncMirroredSlices). */
const SYNC_DEBOUNCE_MS = 120;

export function GroundConfigControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const store = useEditorStore();
  // `tonemapGround` is the one field on this panel that the post pipeline owns
  // rather than the ground: setGroundConfig only forwards it to setPostFX. Read
  // it off the shared slice so the checkbox follows the identical rows in Scene
  // and Post Processing (and a vjson import) live, instead of showing whatever
  // getGroundConfig returned when this panel mounted.
  const postFX = useDomainState("postFX") as PostFXSettings | undefined;
  const [g, setG] = useState<GroundConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pendingSync = useRef(new Set<DomainId>());
  const syncTimer = useRef<number | null>(null);

  useEffect(() => {
    r.getGroundConfig().then(setG).catch((e: Error) => setError(e.message));
  }, [r]);

  useEffect(() => () => { if (syncTimer.current != null) window.clearTimeout(syncTimer.current); }, []);

  if (!g) {
    return error ? <p className="px-3 py-1.5 text-[11px] text-ed-red">{error}</p> : null;
  }

  /**
   * Three of this panel's fields are NOT private to it. Viewer.setGroundConfig
   * forwards `groundReflection` → ground.setReflection, `bakedShadows` →
   * setGroundShadowBake and `tonemapGround` → setPostFX — the exact engine
   * values the store-bound rows in "Ground & Reflection (Legacy)", "Ground
   * Shadow (Baked)" (including its enabledBadge dot) and the Post FX tab's
   * "Tonemap Ground" render off the `scene`/`postFX` slices. Since this panel
   * keeps its own copy, without a re-read those rows keep showing the pre-toggle
   * value and whichever control the user touches second reverts the first.
   * `light.spread`/`light.bias` land in groundShadowBake too, but they are
   * sliders — debounce so a drag doesn't fire one getSceneState per frame.
   */
  const syncMirroredSlices = (patch: GroundConfigPatch) => {
    if (
      patch.groundReflection !== undefined || patch.bakedShadows !== undefined ||
      patch.light?.spread !== undefined || patch.light?.bias !== undefined
    ) pendingSync.current.add("scene");
    if (patch.tonemapGround !== undefined) {
      // This one is also what the checkbox above renders from, so push it into
      // the slice straight away — the debounced re-read below is the
      // authoritative resync, but waiting for it would visibly snap the box
      // back to its old position for the length of the debounce.
      const cur = store.getState("postFX") as PostFXSettings | undefined;
      if (cur) store.setState("postFX", { ...cur, tonemapGround: patch.tonemapGround });
      pendingSync.current.add("postFX");
    }
    if (pendingSync.current.size === 0) return;
    if (syncTimer.current != null) window.clearTimeout(syncTimer.current);
    syncTimer.current = window.setTimeout(() => {
      syncTimer.current = null;
      // Drained into a local first: refresh() is async and a write landing
      // mid-flight must queue a fresh sync rather than be swallowed here.
      const domains = [...pendingSync.current];
      pendingSync.current.clear();
      for (const d of domains) void store.refresh(d);
    }, SYNC_DEBOUNCE_MS);
  };

  const set = (patch: GroundConfigPatch) =>
    r.setGroundConfig(patch).then(
      (next) => {
        setError(null);
        setG(next);
        syncMirroredSlices(patch);
      },
      // A rejected setGroundConfig used to vanish (bare `.then`) leaving the row
      // showing the value the user picked over an engine that never took it.
      (e: Error) => setError(e.message),
    );
  const chk = (label: string, val: boolean, key: keyof GroundConfig, inert?: InertTag) => (
    <Row label={label} inert={inert}><CompactCheck checked={val} onChange={(v) => void set({ [key]: v } as GroundConfigPatch)} /></Row>
  );
  const sld = (label: string, val: number, key: keyof GroundConfig, min: number, max: number, step: number) => (
    <Row label={label}><CompactSlider value={val} min={min} max={max} step={step} onChange={(v) => void set({ [key]: v } as GroundConfigPatch)} /></Row>
  );
  // v31: the ground plane's own surface material (webgi `material` ref).
  const mat = { ...GROUND_MATERIAL_DEFAULT, ...g.material };
  const setMat = (patch: Partial<GroundMaterialParams>) => void set({ material: { ...mat, ...patch } });

  return (
    <div className="flex flex-col">
      {error ? <p className="px-3 py-1 text-[11px] text-ed-red">{error}</p> : null}
      {chk("Visible", g.visible, "visible")}
      <Row label="Size"><CompactNumber value={g.size} width={72} onChange={(v) => void set({ size: v })} /></Row>
      {chk("Render to Depth", g.renderToDepth, "renderToDepth")}
      {chk("Limit Camera", g.limitCameraAboveGround, "limitCameraAboveGround", LIMIT_CAMERA_INERT)}
      {chk("Tonemap", postFX?.tonemapGround ?? g.tonemapGround, "tonemapGround")}
      {sld("Height", g.yOffset, "yOffset", -2, 2, 0.01)}
      {chk("Auto adjust transform", g.autoAdjustTransform, "autoAdjustTransform")}
      {chk("Baked Shadows", g.bakedShadows, "bakedShadows")}
      <Row label="Shadow Frames"><CompactNumber value={g.maxFrameNumber} min={1} max={1000} step={1} width={72} onChange={(v) => void set({ maxFrameNumber: v })} /></Row>
      {chk("Alpha Vignette", g.alphaVignette, "alphaVignette")}
      {chk("Planar Reflections", g.groundReflection, "groundReflection")}
      {chk("Auto Frustum Size", g.autoFrustumSize, "autoFrustumSize")}
      {chk("Physical Reflections", g.physicalReflections, "physicalReflections")}
      {chk("Smooth Shadow", g.smoothShadow, "smoothShadow")}

      {caps.v31 ? (
        <>
          <div className="mt-1 px-3 py-1 text-[12px] font-semibold text-ed-text-2">Plane material</div>
          <Row label="Color"><CompactColor value={mat.color} onChange={(v) => setMat({ color: v })} /></Row>
          <Row label="Roughness"><CompactSlider value={mat.roughness} min={0} max={1} step={0.01} onChange={(v) => setMat({ roughness: v })} /></Row>
          <Row label="Metalness"><CompactSlider value={mat.metalness} min={0} max={1} step={0.01} onChange={(v) => setMat({ metalness: v })} /></Row>
        </>
      ) : null}

      <div className="mt-1 px-3 py-1 text-[12px] font-semibold text-ed-text-2">Randomized light</div>
      <Row label="Color"><CompactColor value={g.light.color} onChange={(v) => void set({ light: { color: v } })} /></Row>
      <Row label="Intensity"><CompactSlider value={g.light.intensity} min={0} max={100} step={0.5} onChange={(v) => void set({ light: { intensity: v } })} /></Row>
      <Row label="Shadow Enabled"><CompactCheck checked={g.light.enabled} onChange={(v) => void set({ light: { enabled: v } })} /></Row>
      <Row label="focus"><CompactSlider value={g.light.focus} min={0} max={1} step={0.01} onChange={(v) => void set({ light: { focus: v } })} /></Row>
      <Row label="spread"><CompactSlider value={g.light.spread} min={0} max={1} step={0.01} onChange={(v) => void set({ light: { spread: v } })} /></Row>
      <Row label="distanceScale"><CompactSlider value={g.light.distanceScale} min={0.01} max={60} step={0.1} onChange={(v) => void set({ light: { distanceScale: v } })} /></Row>
      <Vec3Row label="direction" v={g.light.direction} min={-1} max={1} onChange={(d) => void set({ light: { direction: d } })} />
      <Row label="radius"><CompactSlider value={g.light.radius} min={0.01} max={10} step={0.1} onChange={(v) => void set({ light: { radius: v } })} /></Row>
      <Row label="bias"><CompactSlider value={g.light.bias} min={-0.1} max={0.1} step={0.001} onChange={(v) => void set({ light: { bias: v } })} /></Row>

      <div className="mt-1 px-3 py-1 text-[12px] font-semibold text-ed-text-2">Progressive shadow (directional)</div>
      <Row label="Enabled"><CompactCheck checked={g.progressiveShadow.enabled} onChange={(v) => void set({ progressiveShadow: { enabled: v } })} /></Row>
      <Row label="Intensity"><CompactSlider value={g.progressiveShadow.intensity} min={0} max={50} step={0.1} onChange={(v) => void set({ progressiveShadow: { intensity: v } })} /></Row>
      <Row label="Color" inert={PROG_COLOR_INERT}><CompactColor value={g.progressiveShadow.color} onChange={(v) => void set({ progressiveShadow: { color: v } })} /></Row>
      <Row label="focus"><CompactSlider value={g.progressiveShadow.focus} min={0} max={1} step={0.01} onChange={(v) => void set({ progressiveShadow: { focus: v } })} /></Row>
      <Row label="spread"><CompactSlider value={g.progressiveShadow.spread} min={0} max={1} step={0.01} onChange={(v) => void set({ progressiveShadow: { spread: v } })} /></Row>
      <Row label="distanceScale"><CompactSlider value={g.progressiveShadow.distanceScale} min={0.01} max={60} step={0.1} onChange={(v) => void set({ progressiveShadow: { distanceScale: v } })} /></Row>
      <Vec3Row label="direction" v={g.progressiveShadow.direction} min={-5} max={5} onChange={(d) => void set({ progressiveShadow: { direction: d } })} />
      <Vec3Row label="normalDirection" v={g.progressiveShadow.normalDirection} min={-1} max={1} onChange={(d) => void set({ progressiveShadow: { normalDirection: d } })} />
      <Row label="radius"><CompactSlider value={g.progressiveShadow.radius} min={0.01} max={10} step={0.1} onChange={(v) => void set({ progressiveShadow: { radius: v } })} /></Row>
      <Row label="frustumSize"><CompactSlider value={g.progressiveShadow.frustumSize} min={0.01} max={30} step={0.1} onChange={(v) => void set({ progressiveShadow: { frustumSize: v } })} /></Row>
      <Row label="bias"><CompactSlider value={g.progressiveShadow.bias} min={-0.01} max={0.01} step={0.0001} onChange={(v) => void set({ progressiveShadow: { bias: v } })} /></Row>
    </div>
  );
}

function Vec3Row({ label, v, min, max, onChange }: { label: string; v: Vec3; min: number; max: number; onChange: (v: Vec3) => void }) {
  return (
    <Row label={label} stacked>
      <div className="flex items-center gap-1">
        {(["x", "y", "z"] as const).map((ax) => (
          <CompactNumber key={ax} value={v[ax]} min={min} max={max} step={0.001} width={56} onChange={(nv) => onChange({ ...v, [ax]: nv })} />
        ))}
      </div>
    </Row>
  );
}
